import ErrorFallback from "./components/ErrorFallback/ErrorFallback";
import PageNotFound from "./components/PageNotFound/PageNotFound";
import { PermissionGuard } from "./components/PermissionGuard/PermissionGuard";
import { AuthenticationGuard } from "./components/authentication/AuthenticationGuard/AuthenticationGuard";
import { AuthenticationProvider } from "./components/authentication/AuthenticationProvider/AuthenticationProvider";
import { sentryCreateBrowserRouter } from "./config/sentry";
import { PERMISSIONS } from "./constants/permissions";
import ErrorRoute from "./routes/ErrorRoute";
import LogoutRoute from "./routes/auth/LogoutRoute";
import ProfileRoute from "./routes/profile/ProfileRoute";
import App from "@app/components/App/App";
import { lazyRetry } from "@lib/common/utils/lazyRetry";
import { ErrorBoundary } from "react-error-boundary";
import { Navigate } from "react-router";

const ExperimentDataRoute = lazyRetry(() => import("./routes/experiment/ExperimentDataRoute"));
const DashboardRoute = lazyRetry(() => import("./routes/dashboard/DashboardRoute"));
const ExperimentListRoute = lazyRetry(() => import("./routes/experiment/ExperimentListRoute"));
const ExperimentDetailsRoute = lazyRetry(() => import("./routes/experiment/ExperimentDetailsRoute"));
const ExperimentHomeRoute = lazyRetry(() => import("./routes/experiment/ExperimentHomeRoute"));
const ExperimentHistoryRoute = lazyRetry(() => import("./routes/experiment/ExperimentHistoryRoute"));
const ExperimentResearchRoute = lazyRetry(() => import("./routes/experiment/ExperimentResearchRoute"));
const ExperimentDraftLayoutRoute = lazyRetry(() => import("./routes/experiment/draft/ExperimentDraftLayoutRoute"));
const ExperimentDraftConfigurationRoute = lazyRetry(
  () => import("./routes/experiment/draft/ExperimentDraftConfigurationRoute")
);
const ExperimentConfigurationRoute = lazyRetry(() => import("./routes/experiment/ExperimentConfigurationRoute"));
const DatasetListRoute = lazyRetry(() => import("./routes/dataset/DatasetListRoute"));
const AnalyticsRoute = lazyRetry(() => import("./routes/analytics/AnalyticsRoute"));
const DatasetDetailsRoute = lazyRetry(() => import("./routes/dataset/DatasetDetailsRoute"));
const UserAdminRoute = lazyRetry(() => import("./routes/admin/UserAdminRoute"));
const AuthErrorRoute = lazyRetry(() => import("./routes/auth/AuthErrorRoute"));

export const router = sentryCreateBrowserRouter([
  {
    path: "/auth-error",
    element: <AuthErrorRoute />
  },
  {
    path: "/",
    element: (
      <AuthenticationProvider>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <AuthenticationGuard component={App} />
        </ErrorBoundary>
      </AuthenticationProvider>
    ),
    errorElement: <ErrorRoute />,
    children: [
      { index: true, element: <DashboardRoute /> },
      // login is handled within the authentication guard and then redirected to "/" on success
      { path: "login", element: <></> },
      {
        path: "datasets",
        children: [
          { index: true, element: <DatasetListRoute /> },
          { path: ":datasetId", element: <DatasetDetailsRoute /> }
        ]
      },
      {
        path: "admin",
        element: <PermissionGuard component={UserAdminRoute} requiredPermission={PERMISSIONS.HAS_ACCESS_TO_ORG_ADMIN} />
      },
      { path: "documentation", element: <h1>Documentation</h1> },
      { path: "user", element: <h1>User</h1> },
      { path: "analytics", element: <AnalyticsRoute /> },
      { path: "experiments", element: <ExperimentListRoute /> },
      {
        path: "experiments/drafts/:draftId",
        element: <ExperimentDraftLayoutRoute />,
        children: [{ index: true, element: <ExperimentDraftConfigurationRoute /> }]
      },
      {
        path: "experiments/:experimentId",
        element: <ExperimentDetailsRoute />,
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: "overview", element: <ExperimentHomeRoute /> },
          { path: "data", element: <ExperimentDataRoute /> },
          { path: "configuration", element: <ExperimentConfigurationRoute /> },
          { path: "history", element: <ExperimentHistoryRoute /> },
          { path: "research", element: <ExperimentResearchRoute /> }
        ]
      },
      { path: "profile", element: <AuthenticationGuard component={ProfileRoute} /> },
      { path: "logout", element: <LogoutRoute /> },
      { path: "*", element: <PageNotFound /> }
    ]
  }
]);
